import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export interface Template {
  id: string;
  name: string;
  columns: number;
  cells: { id: string; span?: { col?: number; row?: number } }[];
  preview: React.ReactNode;
}

export const TEMPLATES: Template[] = [
  {
    id: 'single',
    name: '1×1',
    columns: 1,
    cells: [{ id: '1' }],
    preview: (
      <div className="w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm w-full h-full" />
      </div>
    ),
  },
  {
    id: 'vertical-2',
    name: '1×2',
    columns: 1,
    cells: [
      { id: '1' },
      { id: '2' },
    ],
    preview: (
      <div className="grid grid-cols-1 grid-rows-2 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
      </div>
    ),
  },
  {
    id: '2x2',
    name: '2×2',
    columns: 2,
    cells: [
      { id: '1' }, { id: '2' },
      { id: '3' }, { id: '4' },
    ],
    preview: (
      <div className="grid grid-cols-2 gap-0.5 w-full h-full">
        {[1, 2, 3, 4].map((i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
      </div>
    ),
  },
  {
    id: '3x3',
    name: '3×3',
    columns: 3,
    cells: [
      { id: '1' }, { id: '2' }, { id: '3' },
      { id: '4' }, { id: '5' }, { id: '6' },
      { id: '7' }, { id: '8' }, { id: '9' },
    ],
    preview: (
      <div className="grid grid-cols-3 gap-0.5 w-full h-full">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
      </div>
    ),
  },
  {
    id: 'hero',
    name: 'Герой',
    columns: 2,
    cells: [
      { id: '1', span: { col: 2, row: 2 } },
      { id: '2' }, { id: '3' },
    ],
    preview: (
      <div className="grid grid-cols-2 grid-rows-3 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm col-span-2 row-span-2" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
      </div>
    ),
  },
  {
    id: 'sidebar',
    name: 'Боковая',
    columns: 3,
    cells: [
      { id: '1', span: { row: 3 } },
      { id: '2' }, { id: '3' },
      { id: '4' }, { id: '5' },
      { id: '6' }, { id: '7' },
    ],
    preview: (
      <div className="grid grid-cols-3 grid-rows-3 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm row-span-3" />
        {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
      </div>
    ),
  },
  {
    id: 'center',
    name: 'Центр',
    columns: 3,
    cells: [
      { id: '1' }, { id: '2' }, { id: '3' },
      { id: '4' }, { id: '5', span: { col: 1, row: 1 } }, { id: '6' },
      { id: '7' }, { id: '8' }, { id: '9' },
    ],
    preview: (
      <div className="grid grid-cols-3 grid-rows-3 gap-0.5 w-full h-full">
        {[1, 2, 3, 4].map((i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
        <div className="bg-primary/40 rounded-sm" />
        {[6, 7, 8, 9].map((i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
      </div>
    ),
  },
  {
    id: 'strip',
    name: 'Полоса',
    columns: 4,
    cells: [
      { id: '1' }, { id: '2' }, { id: '3' }, { id: '4' },
    ],
    preview: (
      <div className="grid grid-cols-4 gap-0.5 w-full h-full">
        {[1, 2, 3, 4].map((i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
      </div>
    ),
  },
  // Новые шаблоны
  {
    id: '2x3',
    name: '2×3',
    columns: 2,
    cells: [
      { id: '1' }, { id: '2' },
      { id: '3' }, { id: '4' },
      { id: '5' }, { id: '6' },
    ],
    preview: (
      <div className="grid grid-cols-2 grid-rows-3 gap-0.5 w-full h-full">
        {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
      </div>
    ),
  },
  {
    id: '3x2',
    name: '3×2',
    columns: 3,
    cells: [
      { id: '1' }, { id: '2' }, { id: '3' },
      { id: '4' }, { id: '5' }, { id: '6' },
    ],
    preview: (
      <div className="grid grid-cols-3 grid-rows-2 gap-0.5 w-full h-full">
        {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
      </div>
    ),
  },
  {
    id: 'big-left',
    name: 'Большой слева',
    columns: 2,
    cells: [
      { id: '1', span: { row: 2 } },
      { id: '2' },
      { id: '3' },
    ],
    preview: (
      <div className="grid grid-cols-2 grid-rows-2 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm row-span-2" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
      </div>
    ),
  },
  {
    id: 'big-right',
    name: 'Большой справа',
    columns: 2,
    cells: [
      { id: '1' },
      { id: '2', span: { row: 2 } },
      { id: '3' },
    ],
    preview: (
      <div className="grid grid-cols-2 grid-rows-2 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm row-span-2" />
        <div className="bg-muted-foreground/30 rounded-sm" />
      </div>
    ),
  },
  {
    id: 'top-banner',
    name: 'Баннер сверху',
    columns: 3,
    cells: [
      { id: '1', span: { col: 3 } },
      { id: '2' }, { id: '3' }, { id: '4' },
    ],
    preview: (
      <div className="grid grid-cols-3 grid-rows-2 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm col-span-3" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
      </div>
    ),
  },
  {
    id: 'bottom-banner',
    name: 'Баннер снизу',
    columns: 3,
    cells: [
      { id: '1' }, { id: '2' }, { id: '3' },
      { id: '4', span: { col: 3 } },
    ],
    preview: (
      <div className="grid grid-cols-3 grid-rows-2 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm col-span-3" />
      </div>
    ),
  },
  {
    id: 'mosaic',
    name: 'Мозаика',
    columns: 3,
    cells: [
      { id: '1', span: { col: 2 } }, { id: '2' },
      { id: '3' }, { id: '4', span: { col: 2 } },
    ],
    preview: (
      <div className="grid grid-cols-3 grid-rows-2 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm col-span-2" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm col-span-2" />
      </div>
    ),
  },
  {
    id: 'diagonal',
    name: 'Диагональ',
    columns: 2,
    cells: [
      { id: '1', span: { col: 2 } },
      { id: '2' }, { id: '3' },
      { id: '4', span: { col: 2 } },
    ],
    preview: (
      <div className="grid grid-cols-2 grid-rows-3 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm col-span-2" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm col-span-2" />
      </div>
    ),
  },
  {
    id: '4x4',
    name: '4×4',
    columns: 4,
    cells: [
      { id: '1' }, { id: '2' }, { id: '3' }, { id: '4' },
      { id: '5' }, { id: '6' }, { id: '7' }, { id: '8' },
      { id: '9' }, { id: '10' }, { id: '11' }, { id: '12' },
      { id: '13' }, { id: '14' }, { id: '15' }, { id: '16' },
    ],
    preview: (
      <div className="grid grid-cols-4 grid-rows-4 gap-0.5 w-full h-full">
        {Array.from({ length: 16 }, (_, i) => <div key={i} className="bg-muted-foreground/30 rounded-sm" />)}
      </div>
    ),
  },
  {
    id: '1x3',
    name: '1×3',
    columns: 1,
    cells: [
      { id: '1' },
      { id: '2' },
      { id: '3' },
    ],
    preview: (
      <div className="grid grid-cols-1 grid-rows-3 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
      </div>
    ),
  },
  {
    id: '3x1',
    name: '3×1',
    columns: 3,
    cells: [
      { id: '1' }, { id: '2' }, { id: '3' },
    ],
    preview: (
      <div className="grid grid-cols-3 gap-0.5 w-full h-full">
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
        <div className="bg-muted-foreground/30 rounded-sm" />
      </div>
    ),
  },
];

// Шаблоны по умолчанию (первые 3)
const DEFAULT_TEMPLATES = TEMPLATES.slice(0, 3);

interface TemplateSelectorProps {
  selectedTemplate: string;
  onSelectTemplate: (template: Template) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplate,
  onSelectTemplate,
}) => {
  const { t } = useLanguage();
  const [showAll, setShowAll] = useState(false);

  const displayedTemplates = showAll ? TEMPLATES : DEFAULT_TEMPLATES;

  return (
    <div className="space-y-3">
      <h2 className="panel-title font-semibold text-foreground">{t.templates}</h2>
      <div className="grid grid-cols-3 gap-2">
        {displayedTemplates.map((template, index) => (
          <button
            key={template.id}
            onClick={() => onSelectTemplate(template)}
            aria-label={`Template ${template.name}`}
            className={cn(
              "aspect-square p-2 rounded-lg transition-all duration-200 animate-fade-in",
              "border-2 hover:border-primary/50",
              selectedTemplate === template.id
                ? "border-primary bg-primary/10 shadow-[0_0_15px_hsl(190_100%_50%/0.3)]"
                : "border-border bg-card/50 hover:bg-muted"
            )}
            style={{ animationDelay: `${index * 30}ms` }}
            title={template.name}
          >
            {template.preview}
          </button>
        ))}
      </div>
      
      <button
        onClick={() => setShowAll(!showAll)}
        aria-label={showAll ? "Hide templates" : "Show all templates"}
        className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-border bg-card/50 hover:bg-muted transition-colors panel-label text-muted-foreground hover:text-foreground"
      >
        {showAll ? (
          <>
            <ChevronUp className="w-4 h-4" />
            {t.hideTemplates}
          </>
        ) : (
          <>
            <ChevronDown className="w-4 h-4" />
            {t.allTemplates} ({TEMPLATES.length})
          </>
        )}
      </button>
    </div>
  );
};

export default TemplateSelector;